<script setup lang="ts">
    import { MEILISEARCH_MODEL } from '~/app/common/enums/MeilisearchIndexType';
    import type { ProductsIndexSchema } from '~/app/common/schemas/ProductsIndexSchema';
    import { useSearchDropdown } from '~/composables/useSearchDropdown';
    import type { FreeLineData } from '~/stores/aiAssistantTypes';
    import { ModelType } from '~/app/base/schemas/BaseSchema';
    import EndpointFactory from '~/app/factories/EndpointFactory';
    import ProductSchema from '~/app/inventory/schemas/ProductSchema';
    import type ProductUnit from '~/app/inventory/models/ProductUnit';
    import type VatRate from '~/app/invoicing/models/VatRate';
    import type ProductEndpoint from '~/app/inventory/endpoints/ProductEndpoint';

    interface Fields {
        title: string;
        price: number;
        quantity: number;
        selectedUnitId: string | null;
        selectedVatRateId: string | null;
        saveToLibrary: boolean;
    }

    const props = defineProps<{
        initialTitle: string;
    }>();

    const emit = defineEmits<{
        submit: [data: FreeLineData];
        cancel: [];
    }>();

    const { $price } = useNuxtApp();
    const {
        results: productResults,
        showDropdown: showProductDropdown,
        search: searchProducts,
        hideDropdown: hideProductDropdown,
    } = useSearchDropdown<ProductsIndexSchema>({ model: MEILISEARCH_MODEL.PRODUCTS });

    const form = useForm<Fields>({
        title: props.initialTitle,
        price: 0,
        quantity: 1,
        selectedUnitId: null,
        selectedVatRateId: null,
        saveToLibrary: false,
    });
    const isFromCatalog = ref(false);

    const productUnits = ref<ProductUnit[]>([]);
    const vatRates = ref<VatRate[]>([]);

    const productUnitOptions = computed(() =>
        productUnits.value.map((u) => ({ value: u.getId(), text: u.title })),
    );
    const vatRateOptions = computed(() =>
        vatRates.value.map((r) => ({ value: r.getId(), text: `${r.displayRate} — ${r.title}` })),
    );

    onMounted(async () => {
        try {
            const [unitsResponse, ratesResponse] = await Promise.all([
                EndpointFactory.make(ModelType.PRODUCT_UNITS).index(),
                EndpointFactory.make(ModelType.VAT_RATES).index(),
            ]);
            productUnits.value = unitsResponse.data?.get() ?? [];
            vatRates.value = (ratesResponse.data?.get() ?? []).filter((r: VatRate) => r.isSelectable);

            const defaultUnit = productUnits.value.find((u: ProductUnit) => u.title === 'u');
            if (defaultUnit) form.fields.selectedUnitId = defaultUnit.getId();

            const defaultRate = vatRates.value.find((r: VatRate) => r.rate === 21);
            if (defaultRate) form.fields.selectedVatRateId = defaultRate.getId();
        } catch {
            // Dropdowns stay empty, form still works with defaults
        }
    });

    const selectedUnit = computed(() => productUnits.value.find((u: ProductUnit) => u.getId() === form.fields.selectedUnitId));
    const selectedVatRate = computed(() => vatRates.value.find((r: VatRate) => r.getId() === form.fields.selectedVatRateId));

    const triggerSearch = () => {
        searchProducts(form.fields.title);
    };

    const selectProduct = (product: ProductsIndexSchema) => {
        showProductDropdown.value = false;
        productResults.value = [];
        form.fields.title = product.title;
        form.fields.price = product.price / 100;
        form.fields.quantity = product.default_quantity || 1;
        isFromCatalog.value = true;
        form.fields.saveToLibrary = false;

        const matchedUnit = productUnits.value.find((u: ProductUnit) => u.title === product.productUnit);
        if (matchedUnit) form.fields.selectedUnitId = matchedUnit.getId();

        const matchedRate = vatRates.value.find((r: VatRate) => r.rate === product.default_vat_rate);
        if (matchedRate) form.fields.selectedVatRateId = matchedRate.getId();
    };

    const saveProductToLibrary = async () => {
        const schema = new ProductSchema({
            attributes: {
                title: form.fields.title,
                price: Math.round(form.fields.price * 100),
                defaultQuantity: form.fields.quantity,
            },
        });

        if (form.fields.selectedVatRateId) {
            schema.addToOneRelationship('defaultVatRate', ModelType.VAT_RATES, form.fields.selectedVatRateId);
        }
        if (form.fields.selectedUnitId) {
            schema.addToOneRelationship('productUnit', ModelType.PRODUCT_UNITS, form.fields.selectedUnitId);
        }

        try {
            const endpoint = EndpointFactory.make(ModelType.PRODUCTS) as ProductEndpoint;
            await endpoint.store(schema);
        } catch {
            // Silent — line already created, product save is non-blocking
        }
    };

    const submitForm = () => {
        emit('submit', {
            title: form.fields.title,
            price: form.fields.price,
            quantity: form.fields.quantity,
            unit: selectedUnit.value?.title ?? 'u',
            vatRate: selectedVatRate.value ? String(selectedVatRate.value.rate) : '21',
        });

        if (form.fields.saveToLibrary && !isFromCatalog.value) {
            saveProductToLibrary();
        }
    };
</script>

<template>
    <div class="rounded-[12px] border border-[#E7E7E7] bg-white overflow-hidden shadow-[0_1px_2px_rgba(15,16,32,0.04)]">
        <!-- Header band — yellow gradient accent -->
        <div
            class="flex items-center gap-[10px] px-[14px] py-3 border-b border-[#E7E7E7]"
            style="background: linear-gradient(180deg, #FEF8EA 0%, #FEFCF6 100%)"
        >
            <span
                class="w-6 h-6 rounded-full bg-branding-brand-yellow border border-[#E8B102] flex items-center justify-center flex-shrink-0 shadow-sm"
            >
                <i class="fa-solid fa-box text-[11px] text-black" />
            </span>
            <div class="flex-1 min-w-0">
                <p class="font-bricolage font-semibold text-[14px] text-gray-900 leading-[18px]">
                    {{ $t('ai.form.new_product') }}
                </p>
                <p class="font-inter text-[11px] text-gray-400 mt-[1px] truncate">
                    {{ $t('ai.form.from', { seed: initialTitle || $t('ai.form.search_product') }) }}
                </p>
            </div>
        </div>

        <!-- Body -->
        <div class="px-[14px] py-[14px] flex flex-col gap-[12px]">
            <!-- Title (with product search dropdown) -->
            <div class="relative">
                <FormInput
                    :form="form"
                    input-name="title"
                    :placeholder="$t('ai.form.search_product')"
                    @input="triggerSearch"
                    @blur="hideProductDropdown"
                />
                <div
                    v-if="showProductDropdown && productResults.length"
                    class="absolute top-full left-0 right-0 mt-[4px] bg-white border border-[#E7E7E7] rounded-md shadow-md z-10 max-h-[180px] overflow-y-auto"
                >
                    <button
                        v-for="product in productResults"
                        :key="product.uuid"
                        type="button"
                        class="w-full text-left px-3 py-2 hover:bg-[#FEFCF6] border-b border-gray-50 last:border-b-0"
                        @mousedown.prevent="selectProduct(product)"
                    >
                        <span class="block font-inter text-[12px] text-gray-900 truncate">{{ product.title }}</span>
                        <span class="block font-poppins text-[10px] text-gray-400">{{ $price(product.price) }} — {{ product.default_quantity }} {{ product.productUnit }}</span>
                    </button>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-[10px]">
                <FormPriceEuros
                    :form="form"
                    input-name="price"
                />
                <FormInput
                    :form="form"
                    input-name="quantity"
                    input-type="number"
                />
            </div>

            <div class="grid grid-cols-2 gap-[10px]">
                <FormSelect
                    :form="form"
                    input-name="selectedUnitId"
                    :label="$t('form.labels.productUnit')"
                    :options="productUnitOptions"
                />
                <FormSelect
                    :form="form"
                    input-name="selectedVatRateId"
                    :label="$t('form.labels.vat')"
                    :options="vatRateOptions"
                />
            </div>

            <FormCheckbox
                :form="form"
                input-name="saveToLibrary"
                :label="$t('actions.save_in_library')"
                :disabled="isFromCatalog"
            />
        </div>

        <!-- Footer -->
        <div class="flex justify-end gap-2 px-[14px] py-[10px] border-t border-[#E7E7E7] bg-branding-sea-salt">
            <button
                type="button"
                class="px-3 py-[7px] font-inter text-[13px] font-medium text-gray-700 bg-white border border-[#D3D2D2] rounded-md hover:bg-gray-50 shadow-sm cursor-pointer"
                @click="emit('cancel')"
            >
                {{ $t('actions.cancel') }}
            </button>
            <button
                type="button"
                class="px-3 py-[7px] font-inter text-[13px] font-medium text-black bg-branding-brand-yellow border border-[#E8B102] rounded-md hover:bg-branding-mustard shadow-sm cursor-pointer"
                @click="submitForm"
            >
                {{ $t('actions.validate') }}
            </button>
        </div>
    </div>
</template>
